import { Link } from 'react-router-dom';
import { User, ShoppingBag, Store, Menu, X, Shield, ClipboardList } from 'lucide-react';
import { useState } from 'react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import SignInOptions from './SignInOptions';

export default function Header() {
  const {
    user,
    login,
    loginWithApple,
    openSignIn,
    canUseAppleSignIn,
    appleSigningIn,
    demoLoginEnabled,
    loginWithEmail,
    logout,
  } = useAuth();
  const { getItemCount } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const itemCount = getItemCount();

  return (
    <header
      className="fixed left-0 right-0 z-50 glass border-b border-black/5"
      style={{ top: 0, paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="logo-link">
          <img
            src={`${process.env.PUBLIC_URL}/icon.png`}
            alt="SkipdQ"
            className="h-9 w-9 rounded-xl object-cover shrink-0"
          />
          <span className="font-bold text-lg hidden sm:block shrink-0 tracking-tight">
            Skipd<span className="text-primary">Q</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-4">
          {user ? (
            <>
              {user.role === 'admin' && (
                <Link to="/admin">
                  <Button variant="ghost" size="sm" className="gap-2" data-testid="admin-link">
                    <Shield className="w-4 h-4" />
                    Admin
                  </Button>
                </Link>
              )}
              {(user.role === 'vendor' || user.role === 'admin') && (
                <Link to="/dashboard">
                  <Button variant="ghost" size="sm" className="gap-2" data-testid="dashboard-link">
                    <Store className="w-4 h-4" />
                    Dashboard
                  </Button>
                </Link>
              )}
              <Link to="/orders">
                <Button variant="ghost" size="sm" className="gap-2" data-testid="orders-link">
                  <ClipboardList className="w-4 h-4" />
                  Orders
                </Button>
              </Link>
              <Link to="/checkout">
                <Button variant="ghost" size="sm" className="gap-2 relative" data-testid="cart-link">
                  <ShoppingBag className="w-4 h-4" />
                  Cart
                  {itemCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                      {itemCount}
                    </span>
                  )}
                </Button>
              </Link>
              <Link to="/profile">
                <Button variant="ghost" size="sm" className="gap-2" data-testid="profile-link">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                  ) : (
                    <User className="w-4 h-4" />
                  )}
                  <span className="max-w-24 truncate">{user.name?.split(' ')[0]}</span>
                </Button>
              </Link>
            </>
          ) : (
            <Button onClick={openSignIn} size="sm" className="rounded-full" data-testid="signin-btn">
              Sign In
            </Button>
          )}
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          data-testid="mobile-menu-btn"
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-card border-t px-4 py-4 space-y-2">
          {user ? (
            <>
              <Link 
                to="/profile" 
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted"
                onClick={() => setMenuOpen(false)}
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <User className="w-5 h-5" />
                )}
                <span>{user.name}</span>
              </Link>
              {user.role === 'admin' && (
                <Link 
                  to="/admin"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted"
                  onClick={() => setMenuOpen(false)}
                >
                  <Shield className="w-5 h-5" />
                  <span>Admin Panel</span>
                </Link>
              )}
              {(user.role === 'vendor' || user.role === 'admin') && (
                <Link 
                  to="/dashboard"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted"
                  onClick={() => setMenuOpen(false)}
                >
                  <Store className="w-5 h-5" />
                  <span>Dashboard</span>
                </Link>
              )}
              <Link
                to="/orders"
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted"
                onClick={() => setMenuOpen(false)}
                data-testid="mobile-menu-orders"
              >
                <ClipboardList className="w-5 h-5" />
                <span>Orders</span>
              </Link>
              <Link 
                to="/checkout"
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted"
                onClick={() => setMenuOpen(false)}
              >
                <ShoppingBag className="w-5 h-5" />
                <span>Cart ({itemCount})</span>
              </Link>
              <button 
                onClick={() => { logout(); setMenuOpen(false); }}
                className="w-full text-left flex items-center gap-3 p-3 rounded-lg hover:bg-muted text-destructive"
              >
                Sign Out
              </button>
            </>
          ) : (
            <SignInOptions
              onGoogle={login}
              onApple={async () => {
                if (await loginWithApple()) setMenuOpen(false);
              }}
              showApple={canUseAppleSignIn}
              appleLoading={appleSigningIn}
              showEmail={demoLoginEnabled}
              onEmail={async (email, password) => {
                const ok = await loginWithEmail(email, password);
                if (ok) setMenuOpen(false);
                return ok;
              }}
              onLegalNavigate={() => setMenuOpen(false)}
            />
          )}
        </div>
      )}
    </header>
  );
}
